import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import {
  Activity as ActivityIcon,
  ChevronsUpDown,
  Eye,
  FileText,
  History,
  LayoutGrid,
  LogOut,
  PackageOpen,
  RefreshCw,
  Settings,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Mark, TenantAvatar, Wordmark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { useEditorStatus } from "@/context/EditorStatusContext";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";

const AutosavePill = () => {
  const { saveState, lastSavedAt } = useEditorStatus();
  const map = {
    idle: { text: lastSavedAt ? `Saved ${timeAgo(lastSavedAt)}` : "All changes saved", dot: "bg-[hsl(var(--success))]" },
    saving: { text: "Saving\u2026", dot: "bg-[hsl(var(--rp-blue))] animate-pulse" },
    saved: { text: "Saved", dot: "bg-[hsl(var(--success))]" },
    error: { text: "Could not save", dot: "bg-[hsl(var(--destructive))]" },
  };
  const s = map[saveState] || map.idle;
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-white px-3 py-1 text-xs text-[hsl(var(--muted-foreground))]"
      aria-live="polite"
      data-testid="autosave-status-pill"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
      {s.text}
    </span>
  );
};

const TenantSwitcher = ({ current }) => {
  const { tenants } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  if (tenants.length <= 1) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="bg-white"
          data-testid="tenant-switcher"
          aria-label="Switch client"
        >
          Switch client
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <Command>
          <CommandInput placeholder={"Find a client\u2026"} data-testid="tenant-switcher-search" />
          <CommandList>
            <CommandEmpty>No clients match.</CommandEmpty>
            <CommandGroup>
              {tenants.map((t) => (
                <CommandItem
                  key={t.slug}
                  value={`${t.name} ${t.slug}`}
                  onSelect={() => {
                    setOpen(false);
                    navigate(`/t/${t.slug}`);
                  }}
                  data-testid={`tenant-switcher-item-${t.slug}`}
                >
                  <TenantAvatar tenant={t} className="mr-2 h-6 w-6" />
                  <span className="truncate">{t.name}</span>
                  {t.slug === current ? (
                    <Badge variant="secondary" className="ml-auto font-normal">
                      Current
                    </Badge>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const tenantTabs = (isAgency) =>
  [
    { to: "", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/edit", label: "Edit content", icon: FileText },
    { to: "/review", label: "Review & publish", icon: ShieldCheck },
    { to: "/preview", label: "Preview", icon: Eye },
    { to: "/history", label: "History", icon: History },
    { to: "/activity", label: "Activity", icon: ActivityIcon },
    isAgency ? { to: "/sync", label: "Sync from site", icon: RefreshCw } : null,
    isAgency ? { to: "/handover", label: "Handover", icon: PackageOpen } : null,
    isAgency ? { to: "/settings", label: "Settings", icon: Settings } : null,
  ].filter(Boolean);

/**
 * Which lens the agency is looking through, and a one-click way to change it.
 *
 * Only an agency account has two views, so only an agency account sees this. A client editor has
 * nothing to switch between and is shown nothing.
 */
const ViewModeSwitch = () => {
  const { isAgencyAccount, viewMode, setViewMode } = useAuth();
  const navigate = useNavigate();
  const params = useParams();

  if (!isAgencyAccount) return null;

  const choose = (next) => {
    if (!next || next === viewMode) return;
    setViewMode(next);
    // Agency-only pages stop existing in client view, so move somewhere a client could
    // genuinely be - this client if we are on one, otherwise let the router decide.
    if (next === "client") {
      navigate(params.slug ? `/t/${params.slug}` : "/", { replace: true });
    }
  };

  return (
    <ToggleGroup
      type="single"
      value={viewMode}
      onValueChange={choose}
      aria-label="Switch between agency view and client view"
      className="hidden rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-0.5 sm:flex"
      data-testid="view-mode-switch"
    >
      <ToggleGroupItem
        value="agency"
        aria-label="Agency view"
        title="Agency view - everything, including setup and handover"
        className="h-7 gap-1.5 rounded-md px-2.5 text-xs data-[state=on]:bg-white data-[state=on]:text-[hsl(var(--rp-navy))] data-[state=on]:shadow-sm"
        data-testid="view-mode-agency"
      >
        <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
        Agency view
      </ToggleGroupItem>
      <ToggleGroupItem
        value="client"
        aria-label="Client view"
        title="Client view - only what the client can see"
        className="h-7 gap-1.5 rounded-md px-2.5 text-xs data-[state=on]:bg-white data-[state=on]:text-[hsl(var(--warning))] data-[state=on]:shadow-sm"
        data-testid="view-mode-client"
      >
        <Eye className="h-3.5 w-3.5" aria-hidden="true" />
        Client view
      </ToggleGroupItem>
    </ToggleGroup>
  );
};

/**
 * Unmissable while the agency is looking through the client's eyes, so nobody mistakes a
 * deliberately reduced screen for a broken one.
 */
const ClientViewBanner = () => {
  const { isAgencyAccount, viewMode, setViewMode, tenants } = useAuth();
  const params = useParams();

  if (!isAgencyAccount || viewMode !== "client") return null;
  const tenant = tenants.find((t) => t.slug === params.slug);

  return (
    <div
      className="border-b border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10"
      data-testid="client-view-banner"
    >
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 sm:px-6 lg:px-8">
        <Eye className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--warning))]" aria-hidden="true" />
        <p className="text-xs leading-5">
          <span className="font-semibold">Client view.</span>{" "}
          <span className="text-[hsl(var(--muted-foreground))]">
            This is everything {tenant ? tenant.name : "your client"} can see. Setup, handover and
            the read address are hidden.
          </span>
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setViewMode("agency")}
          className="ml-auto h-7 bg-white px-2.5 text-xs"
          data-testid="exit-client-view-button"
        >
          Back to agency view
        </Button>
      </div>
    </div>
  );
};

export const AppShell = ({ children }) => {
  const { user, tenants, isAgency, isAgencyAccount, viewMode, setViewMode, logout, agencyName } =
    useAuth();
  const { pending, setPending } = useEditorStatus();
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.slug;
  const tenant = tenants.find((t) => t.slug === slug);

  useEffect(() => {
    if (!slug) {
      setPending(null);
      return;
    }
    let alive = true;
    api
      .get(`/tenants/${slug}/pending`)
      .then(({ data }) => {
        if (alive) setPending(data.count);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [slug, setPending]);

  const onSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="App min-h-screen bg-[hsl(var(--background))]">
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Link
            to={isAgency ? "/" : slug ? `/t/${slug}` : "/"}
            className="rp-focus flex shrink-0 items-center gap-2 rounded-md"
            data-testid="header-home-link"
          >
            <Mark className="h-8 w-8 sm:hidden" />
            <Wordmark
              size="sm"
              tone="on-light"
              label={agencyName}
              className="hidden sm:inline-flex"
            />
          </Link>

          {tenant ? (
            <>
              <span className="h-6 w-px bg-[hsl(var(--border))]" aria-hidden="true" />
              <div className="flex min-w-0 items-center gap-2">
                <TenantAvatar tenant={tenant} className="h-8 w-8" />
                <div className="min-w-0">
                  <p
                    className="truncate text-sm font-semibold leading-tight"
                    data-testid="header-tenant-name"
                  >
                    {tenant.name}
                  </p>
                  <a
                    href={tenant.site_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="rp-focus truncate rounded text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--rp-blue-2))]"
                    data-testid="header-tenant-site-link"
                  >
                    {(tenant.site_url || "").replace(/^https?:\/\//, "")}
                  </a>
                </div>
              </div>
            </>
          ) : null}

          <div className="ml-auto flex items-center gap-2">
            {slug ? <AutosavePill /> : null}
            {slug && pending ? (
              <Button
                size="sm"
                onClick={() => navigate(`/t/${slug}/review`)}
                className="bg-[hsl(var(--rp-navy))] hover:bg-[hsl(var(--rp-navy-2))]"
                data-testid="review-and-publish-button"
              >
                Review &amp; publish
                <Badge className="ml-2 bg-white text-[hsl(var(--rp-navy))] hover:bg-white">
                  {pending}
                </Badge>
              </Button>
            ) : null}
            {isAgency ? <TenantSwitcher current={slug} /> : null}
            <ViewModeSwitch />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  data-testid="user-menu-trigger"
                  aria-label="Account menu"
                >
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-[hsl(var(--rp-navy))] text-[11px] font-semibold text-white">
                    {(user?.display_name || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <span className="hidden text-xs font-medium md:block">
                    {isAgencyAccount ? "Agency Admin" : user?.display_name}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal text-[hsl(var(--muted-foreground))]">
                  Signed in as {isAgencyAccount ? "Agency Admin" : user?.display_name}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAgencyAccount ? (
                  <>
                    {/* Mirrors the header switch, which is hidden on narrow screens. */}
                    <DropdownMenuItem
                      onClick={() => setViewMode(viewMode === "client" ? "agency" : "client")}
                      className="sm:hidden"
                      data-testid="menu-toggle-view-mode"
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      {viewMode === "client" ? "Back to agency view" : "Switch to client view"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="sm:hidden" />
                  </>
                ) : null}
                {isAgency ? (
                  <>
                    <DropdownMenuItem asChild data-testid="menu-all-clients">
                      <Link to="/">
                        <LayoutGrid className="mr-2 h-4 w-4" />
                        All clients
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-invites">
                      <Link to="/admin/invites">
                        <UserPlus className="mr-2 h-4 w-4" />
                        Who has access
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : null}
                <DropdownMenuItem onClick={onSignOut} data-testid="sign-out-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {slug ? (
          <nav
            className="mx-auto flex max-w-[1600px] gap-1 overflow-x-auto px-4 pb-1 sm:px-6 lg:px-8 rp-scroll"
            aria-label="Client sections"
          >
            {tenantTabs(isAgency).map((tab) => (
              <NavLink
                key={tab.to}
                to={`/t/${slug}${tab.to}`}
                end={tab.end}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-1.5 rounded-t-md border-b-2 px-3 py-2 text-sm transition-colors duration-150 ${
                    isActive
                      ? "border-b-[hsl(var(--rp-blue))] font-medium text-[hsl(var(--foreground))]"
                      : "border-b-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  }`
                }
                data-testid={`tenant-tab-${tab.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <tab.icon className="h-3.5 w-3.5" aria-hidden="true" />
                {tab.label}
              </NavLink>
            ))}
          </nav>
        ) : null}

        <ClientViewBanner />
      </header>

      <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>

      <footer className="mx-auto max-w-[1600px] px-4 pb-8 sm:px-6 lg:px-8">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          Private editor - not linked publicly and blocked from search engines.
        </p>
      </footer>
    </div>
  );
};
